import { themeColors } from '@juvare/common-web-ui/JuvareTheme';
import { Box, FormControl, Radio, RadioGroup, Tooltip } from '@mui/material';
import React from 'react';
/**
 * A component that displays a list of selectable icons as radio buttons.
 * Each option is presented as a card with an icon, label, and corresponding radio input.
 *
 * @component
 *
 * @param {RadioIconPickerProps} props - Props for configuring the radio icon picker.
 * @param {RadioIcon[]} [props.items] - Array of radio icon options to display.
 * @param {string} [props.selectedItemValue] - The `value` of the currently selected item.
 * @param {(item?: RadioIcon) => void} props.onSelectItem - Callback when an item is selected.
 * @param {string} [props.'data-test'] - Test ID for the root component.
 * @param {string} [props.ariaDescribedBy] - ARIA `aria-describedby` attribute for accessibility.
 *
 * @returns {React.ReactElement} A group of cards with radio buttons that function as a picker.
 */
export const RadioItemPicker = (props) => {
    return (React.createElement(RadioGroup, { row: true }, props.items.map((item, index) => {
        var _a;
        const radio = (React.createElement(Radio, { value: item.value, checked: item.value === props.selectedItemValue, onClick: () => props.onSelectItem(item), icon: React.createElement(RadioItemBox, { checked: item.value === props.selectedItemValue, element: item.element }), checkedIcon: React.createElement(RadioItemBox, { checked: item.value === props.selectedItemValue, element: item.element }), inputProps: { 'aria-label': item['aria-label'], 'aria-describedby': props.ariaDescribedBy }, TouchRippleProps: {
                center: false,
            }, autoFocus: ((_a = props.autoFocus) !== null && _a !== void 0 ? _a : false) && index === 0 }));
        return (React.createElement(FormControl, { key: item.value, sx: {
                '.MuiRadio-root': {
                    borderRadius: 1,
                    p: 1,
                },
            } }, item.tooltip ? (React.createElement(Tooltip, { title: item.tooltip, arrow: true, PopperProps: { modifiers: [{ name: 'offset', options: { offset: [0, -8] } }] } }, radio)) : (radio)));
    })));
};
const RadioItemBox = (props) => {
    const uncheckedIconSx = { p: '8px', border: '1px solid', borderColor: themeColors.border.main };
    const checkedIconSx = { p: '7px', border: '2px solid', borderColor: themeColors.primary.base };
    return React.createElement(Box, { sx: Object.assign(Object.assign({}, (props.checked ? checkedIconSx : uncheckedIconSx)), { borderRadius: 1 }) }, props.element);
};
